import { useState, useEffect } from 'react'
import { listConnections, listCrmObjects, triggerSync, listCrmDatasets } from '../lib/api'
import { useLang } from '../lib/useLang'

export default function CrmImport({ userId, onImport }) {
  const { t } = useLang()
  const [connections, setConnections] = useState([])
  const [selectedConn, setSelectedConn] = useState(null)
  const [objects, setObjects] = useState([])
  const [selectedObjects, setSelectedObjects] = useState([])
  const [syncing, setSyncing] = useState(false)

  useEffect(() => {
    listConnections(userId).then(setConnections).catch(() => {})
  }, [userId])

  const handleSelectConnection = async (conn) => {
    setSelectedConn(conn)
    try {
      const { objects: objs } = await listCrmObjects(conn.id)
      setObjects(objs)
    } catch { setObjects([]) }
    setSelectedObjects([])
  }

  const handleToggleObject = (obj) => {
    setSelectedObjects(prev =>
      prev.includes(obj) ? prev.filter(o => o !== obj) : [...prev, obj]
    )
  }

  const handleImport = async () => {
    setSyncing(true)
    try {
      await triggerSync(selectedConn.id, userId, selectedObjects)
      let datasets = []
      for (let i = 0; i < 30; i++) {
        await new Promise(r => setTimeout(r, 2000))
        datasets = await listCrmDatasets(selectedConn.id, userId)
        if (datasets.length >= selectedObjects.length) break
      }
      const tables = datasets.map(ds => ({
        name: ds.filename,
        dataset_path: ds.path,
        columns: [],
        source: 'crm',
        crm_connection_id: selectedConn.id,
      }))
      onImport(tables)
    } catch (e) {
      console.error('CRM import failed', e)
    }
    setSyncing(false)
  }

  if (connections.length === 0) {
    return (
      <div className="text-center py-8 text-ink">
        <p>{t('crmImport.noCrm')}</p>
        <a href="/settings/integrations" className="text-accent hover:text-accent underline">{t('crmImport.connectCrm')}</a>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {connections.map(c => (
          <button
            key={c.id}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              selectedConn?.id === c.id
                ? 'bg-accent/15 text-ink'
                : 'border border-ink/10 text-ink hover:bg-ink/[0.05] hover:text-ink'
            }`}
            onClick={() => handleSelectConnection(c)}
          >
            {c.provider}
          </button>
        ))}
      </div>
      {selectedConn && (
        <div>
          <p className="font-medium mb-2 text-ink">{t('crmImport.selectObjects')}</p>
          <div className="flex flex-wrap gap-2">
            {objects.map(obj => (
              <label key={obj} className="flex items-center gap-1.5 cursor-pointer text-ink">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm checkbox-primary"
                  checked={selectedObjects.includes(obj)}
                  onChange={() => handleToggleObject(obj)}
                />
                <span>{obj}</span>
              </label>
            ))}
          </div>
          <button
            className="mt-4 rounded-lg bg-accent/15 px-4 py-1.5 text-sm font-medium text-ink transition-colors hover:bg-accent/15 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={selectedObjects.length === 0 || syncing}
            onClick={handleImport}
          >
            {syncing ? <><span className="loading loading-spinner loading-xs" /> {t('crmImport.importing')}</> : t('crmImport.importObjects', { count: selectedObjects.length })}
          </button>
        </div>
      )}
    </div>
  )
}
